(() => {
  if (window.OcDoofinderTracking || !window.OcShopify?.trackItemPayload) return;
  window.OcDoofinderTracking = true;

  const CARD = '.dfd-results-grid .dfd-card';
  const PENDING_SELECTION = 'oc:search-selection:v1';
  const cache = new Map();
  const queue = [];
  let activeRequests = 0;
  let root;
  let timer;
  let generation = 0;
  let resultKeys = [];
  let resultQuery = '';
  let seen = new Set();
  let pendingViews = new Map();
  let flushTimer;
  let navigation = 0;

  function clean(payload) {
    return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ''));
  }

  function push(event, items) {
    if (!items.length) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce: { items } });
  }

  function context(card) {
    const link = card.querySelector('a.dfd-card-link[href], a[href*="/products/"]');
    const href = link?.href || card.getAttribute('dfd-value-link');
    if (!href) return null;
    let target;
    try { target = new URL(href, window.location.origin); } catch (error) { return null; }
    const productHandle = window.OcItemLists.productHandle(target.href);
    if (!productHandle) return null;
    // Use the current storefront/locale even when the search index has absolute URLs.
    const url = new URL(`${window.Shopify?.routes?.root || '/'}products/${productHandle}`, window.location.origin);
    if (target.searchParams.has('variant')) url.searchParams.set('variant', target.searchParams.get('variant'));
    url.searchParams.set('section_id', 'on-add-product-info');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const cards = Array.from(card.closest('.dfd-results-grid').querySelectorAll('.dfd-card'));
    const index = Number(link?.getAttribute('dfd-value-position') || card.getAttribute('data-index')) || cards.indexOf(card) + 1;
    const searchTerm = root?.querySelector('input.dfd-searchbox-input, .dfd-search-input input')?.value?.trim() || '';
    return {
      url: url.href,
      key: `${url.href}|${window.Shopify?.currency?.active || ''}`,
      productHandle,
      index,
      searchTerm,
      href: target.href,
      itemName: card.querySelector('.dfd-card-title, .dfd-title-sm, [class*="card-title"]')?.textContent.trim(),
    };
  }

  function payload(dataset, info) {
    return clean({
      ...window.OcShopify.trackItemPayload(dataset, ['item_sku', 'item_variant']),
      item_list_name: 'search results',
      index: info.index,
      search_term: info.searchTerm,
    });
  }

  function pump() {
    while (activeRequests < 4 && queue.length) {
      const job = queue.shift();
      activeRequests++;
      job().finally(() => { activeRequests--; pump(); });
    }
  }

  function load(info) {
    const existing = cache.get(info.key);
    if (existing && (!existing.failedAt || Date.now() - existing.failedAt < 30000)) return existing;
    const entry = {};
    cache.set(info.key, entry);
    entry.promise = new Promise(resolve => {
      queue.push(async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(info.url, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const node = html.querySelector('.js_fetched-product-list-info') || html.querySelector('.js_fetched-product-info');
          if (!node?.dataset.productId || node.dataset.productHandle !== info.productHandle || !node.dataset.price) {
            throw new Error('Missing product tracking data');
          }
          entry.data = { ...node.dataset };
          resolve(entry.data);
        } catch (error) {
          entry.failedAt = Date.now();
          console.warn('[Doofinder tracking] Product data unavailable:', info.productHandle, error.message);
          resolve(null);
        } finally {
          window.clearTimeout(timeout);
        }
      });
      pump();
    });
    // Keep this page's cache bounded. Never evict requests that are still pending.
    if (cache.size > 200) {
      for (const [key, value] of cache) {
        if (cache.size <= 200) break;
        if (value.data || value.failedAt) cache.delete(key);
      }
    }
    return entry;
  }

  function visible(card, margin = 0) {
    if (!card.isConnected || card.closest('[hidden], [aria-hidden="true"], .oc-doofinder-result-hidden')) return false;
    const rect = card.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    for (let node = card; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    }
    const width = Math.max(0, Math.min(rect.right, window.innerWidth + margin) - Math.max(rect.left, -margin));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight + margin) - Math.max(rect.top, -margin));
    return width * height / (rect.width * rect.height) >= (margin ? 0.01 : 0.25);
  }

  function flushViews() {
    const items = Array.from(pendingViews.values()).sort((a, b) => a.index - b.index);
    pendingViews.clear();
    for (let i = 0; i < items.length; i += 10) push('view_item_list', items.slice(i, i + 10));
  }

  function resetResults() {
    generation++;
    seen = new Set();
    pendingViews.clear();
    resultKeys = [];
    resultQuery = '';
    window.clearTimeout(flushTimer);
  }

  function scan() {
    refreshRoot();
    const cards = Array.from(document.querySelectorAll(CARD));
    const entries = cards.map(card => ({ card, info: context(card) })).filter(entry => entry.info);
    const query = entries[0]?.info.searchTerm || '';
    const keys = entries.map(({ info }) => `${info.key}|${info.index}`);
    // Appending a page keeps existing impressions; filtering/reordering starts a new list.
    if (query !== resultQuery || !resultKeys.every((key, i) => keys[i] === key)) resetResults();
    resultKeys = keys;
    resultQuery = query;
    const currentGeneration = generation;
    entries.forEach(({ card, info }, i) => {
      if (!visible(card, 500)) return;
      const entry = load(info);
      entry.promise.then(dataset => {
        if (!dataset || currentGeneration !== generation || !visible(card)) return;
        const current = context(card);
        if (current?.key !== info.key || current.searchTerm !== info.searchTerm || current.index !== info.index) return;
        const key = keys[i];
        if (seen.has(key)) return;
        seen.add(key);
        pendingViews.set(key, payload(dataset, info));
        window.clearTimeout(flushTimer);
        flushTimer = window.setTimeout(flushViews, 100);
      });
    });
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(scan, 120);
  }

  const observer = new MutationObserver(schedule);
  function refreshRoot() {
    const next = document.querySelector('.dfd-root');
    if (next === root) return;
    observer.disconnect();
    root = next;
    resetResults();
    if (root) observer.observe(root, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'href', 'dfd-value-link', 'dfd-value-position', 'data-value'],
    });
  }

  function readSelection() {
    try { return JSON.parse(window.sessionStorage.getItem(PENDING_SELECTION) || 'null'); } catch (error) { return null; }
  }

  function clearSelection() {
    try { window.sessionStorage.removeItem(PENDING_SELECTION); } catch (error) {}
  }

  function select(dataset, info) {
    window.OcItemLists.remember(dataset, 'search results');
    flushViews();
    push('select_item', [payload(dataset, info)]);
  }

  function trackSelection(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const card = event.target.closest?.(CARD);
    if (!card) return;
    const link = event.target.closest('a[href]');
    // Doofinder makes the whole card clickable; controls inside it are not product selections.
    if (event.target.closest('button, input, select, textarea') || (link && !window.OcItemLists.productHandle(link.href))) return;
    const info = context(card);
    if (!info) return;
    window.OcItemLists.remember({ productHandle: info.productHandle }, 'search results');
    const entry = load(info);
    if (entry.data) {
      select(entry.data, info);
      return;
    }

    // Persist before Doofinder navigates. On a cold/slow request the PDP can finish
    // the event from its SSR data without delaying navigation or inventing IDs.
    const sameTab = event.type !== 'auxclick' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey &&
      (!link?.target || link.target === '_self') && new URL(info.href).origin === window.location.origin;
    const pending = { ...info, at: Date.now() };
    const currentNavigation = navigation;
    if (sameTab) {
      try { window.sessionStorage.setItem(PENDING_SELECTION, JSON.stringify(pending)); } catch (error) {}
    }
    entry.promise.then(dataset => {
      if (!dataset || currentNavigation !== navigation) return;
      if (sameTab && readSelection()?.at === pending.at) clearSelection();
      select(dataset, info);
    });
  }

  function restoreSelection() {
    const pending = readSelection();
    if (!pending) return;
    clearSelection();
    if (Date.now() - pending.at > 30000 || window.OcItemLists.productHandle(window.location.href) !== pending.productHandle) return;
    const dataset = document.querySelector('product-form .js_gtm-track')?.dataset;
    if (dataset?.productHandle === pending.productHandle) select(dataset, pending);
  }

  function init() {
    restoreSelection();
    refreshRoot();
    new MutationObserver(schedule).observe(document.body, { childList: true });
    schedule();
  }

  document.addEventListener('click', trackSelection, true);
  document.addEventListener('auxclick', trackSelection, true);
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pagehide', () => { navigation++; });
  ['doofinder.layer.load', 'doofinder.layer.search', 'doofinder.layer.update', 'doofinder.show', 'doofinder.search', 'doofinder.load'].forEach(name => {
    document.addEventListener(name, schedule);
  });
  document.addEventListener('doofinder.hide', resetResults);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
